import Box from '@enlighten/design-system/dist/Box';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import type { Program } from './programs';

interface ProgramCardProps {
	program: Program;
	isActive: boolean;
	isNext: boolean;
	panelId: string;
	onSelect: () => void;
	onProgressEnd: () => void;
}

const BORDER_WIDTH = 2;
/** 테두리가 한 바퀴 채워지는 시간 = 다음 카드로 넘어가는 주기 */
const PROGRESS_DURATION_MS = 5000;

const ProgramCard: React.FC<ProgramCardProps> = ({ program, isActive, isNext, panelId, onSelect, onProgressEnd }) => {
	const { number, title, theme, chip } = program;
	const { ref, inView } = useInView({ threshold: 0.5 });

	return (
		<Box ref={ref} position="relative">
			<Stack
				component="button"
				type="button"
				aria-pressed={isActive}
				aria-controls={panelId}
				onClick={onSelect}
				position="relative"
				alignItems="center"
				justifyContent="center"
				width="100%"
				height="174px"
				border={`${BORDER_WIDTH}px solid ${isActive ? theme.main : '#F2F3F5'}`}
				borderRadius="16px"
				bgcolor={isActive ? theme.surface : '#FFFFFF'}
				boxShadow="0px 4px 28px 0px rgba(0, 0, 0, 0.1)"
				overflow="hidden"
				sx={{ padding: 0, cursor: 'pointer', appearance: 'none' }}
			>
				{isActive ? null : (
					<Box
						position="absolute"
						right="-20px"
						bottom="0"
						fontSize="208px"
						lineHeight={1}
						color="#F2F3F5"
						sx={{ transform: 'translateY(40%)', fontFamily: '"New Peninim MT", Georgia, "Times New Roman", serif', fontStyle: 'italic' }}
					>
						{number}
					</Box>
				)}
				<Typography position="relative" paddingX="20px" fontSize="28px" fontWeight={isActive ? 700 : 500} color={isActive ? theme.main : '#6F7276'} textAlign="center" sx={{ wordBreak: 'keep-all' }}>
					{title}
				</Typography>
			</Stack>

			{/* 버튼에 overflow hidden 이 걸려 있어 테두리가 잘리므로 버튼 밖에 겹친다 */}
			{isNext ? (
				<Box
					component="svg"
					position="absolute"
					top={`${BORDER_WIDTH / 2}px`}
					left={`${BORDER_WIDTH / 2}px`}
					width={`calc(100% - ${BORDER_WIDTH}px)`}
					height={`calc(100% - ${BORDER_WIDTH}px)`}
					sx={{
						pointerEvents: 'none',
						// svg 기본값이 overflow hidden 이라 스트로크 바깥 절반이 잘린다
						overflow: 'visible',
						'& rect': {
							strokeDasharray: 1,
							strokeDashoffset: 1,
							animation: `programCardProgress ${PROGRESS_DURATION_MS}ms linear forwards`,
							// 화면 밖에서 혼자 다 돌지 않도록 (진행률은 유지한 채 멈춤)
							animationPlayState: inView ? 'running' : 'paused',
						},
						'@keyframes programCardProgress': {
							from: { strokeDashoffset: 1 },
							to: { strokeDashoffset: 0 },
						},
						// 자동 순환의 신호가 이 애니메이션이라 재생을 끄면 순환도 멈춘다
						'@media (prefers-reduced-motion: reduce)': { '& rect': { animation: 'none' } },
					}}
				>
					{/*
						- rx 15 = 라운드 16px 의 테두리 중심선 반지름 (선택 상태 테두리와 정확히 겹침)
						- pathLength 로 둘레 0~1 정규화 → 카드 폭이 flex 라도 실측 불필요
					*/}
					<rect width="100%" height="100%" rx={15} pathLength="1" fill="none" stroke={theme.main} strokeWidth={BORDER_WIDTH} onAnimationEnd={onProgressEnd} />
				</Box>
			) : null}

			{isActive ? (
				<Stack
					position="absolute"
					top="-24px"
					left="50%"
					alignItems="center"
					justifyContent="center"
					height="48px"
					padding="0 18px"
					bgcolor={theme.main}
					borderRadius="500px"
					sx={{ transform: 'translateX(-50%)' }}
				>
					<Typography fontSize="22px" fontWeight={700} color="#FFFFFF" whiteSpace="nowrap">
						{chip}
					</Typography>
				</Stack>
			) : null}
		</Box>
	);
};

export default ProgramCard;
